import { Card, Select, Text } from "@ttc3k/trekker";
import { useCallback, useMemo, useState } from "react";
import { useBookerContext, useCartContext } from "widgets/booker/contexts";
import { CollapsableStep } from "widgets/booker/shared";
import { NumericStepper } from "widgets/booker/shared/NumericStepper";
import { useTranslation } from "react-i18next";
import { formatPrice, getMultilingualString } from "shared/utils";
import { EnumProductType, Product } from "gql/generated";
import {
  getImageDimension,
  getStockSignal,
  getValueImages,
  isDefaultMerchVariant,
} from "shared/merchCatalog";
import { SmartImage } from "shared/components";
import { getCartVariantQuantities } from "./getCartVariantQuantities";
import { Box, Flex } from "styled-system/jsx";
import { cva } from "styled-system/css";
import { styled } from "styled-system/jsx";

type Lang = "en" | "es" | "fr" | undefined;

const THUMBNAIL_SIZE = 48;

type MerchDimension = {
  _id: string;
  name: { en?: string | null; es?: string | null; fr?: string | null };
  values: Array<{
    _id: string;
    label: { en?: string | null; es?: string | null; fr?: string | null };
  }>;
};

type MerchVariant = {
  _id: string;
  dimensionValueIds: string[];
  label: string;
  price: number;
  inventoryCount: number;
  isActive: boolean;
};

const cardStyles = cva({
  base: {
    width: "full",
    outline: "0 transparent solid",
    transitionDuration: "normal",
    transitionProperty: "border, outline, opacity",
    transitionTimingFunction: "default",
    boxShadow: "none",
  },
  variants: {
    visual: {
      default: {},
      active: {
        borderColor: "border.dark",
        outlineWidth: "1px",
        outlineColor: "border.dark",
      },
      soldOut: {
        opacity: 0.5,
      },
    },
  },
  defaultVariants: { visual: "default" },
});

const MerchCardSurface = styled(Card.Surface, cardStyles);

export const MerchVariantSelection = () => {
  const { product } = useBookerContext();
  const { cart } = useCartContext();
  const { t } = useTranslation("booker", { keyPrefix: "MERCH_VARIANT" });

  const { dimensions, variants } = getMerchOptions(product);
  const hasItemsInCart = cart.some(
    item =>
      item.productType === EnumProductType.Merch && !!item.merchVariantId,
  );
  const hasOptions = dimensions.length > 0;

  if (!hasOptions && !variants.some(isDefaultMerchVariant)) return null;

  return (
    <CollapsableStep
      title={hasOptions ? t("TITLE") : t("QUANTITY_TITLE")}
      complete={hasItemsInCart}
      stepId="merch-selection"
    >
      <Text color="text.mid">
        {hasOptions ? t("SUBTITLE") : t("QUANTITY_SUBTITLE")}
      </Text>
      <MerchVariantOptions product={product} />
    </CollapsableStep>
  );
};

/** One card per primary dimension value; quantity changes write straight to the cart. */
export const MerchVariantOptions = ({
  product,
  onImageValueChange,
}: {
  product: Product | null;
  /** Called with the image-driving value (e.g. "Red") the shopper is working with. */
  onImageValueChange?: (valueId: string) => void;
}) => {
  const { language } = useBookerContext();
  const { cart } = useCartContext();

  const { dimensions, variants } = getMerchOptions(product);
  const allVariants = variants.filter(v => v.isActive !== false);

  const cartVariantQuantities = useMemo(
    () => getCartVariantQuantities(cart),
    [cart],
  );

  if (dimensions.length === 0) {
    if (!product || !allVariants.some(isDefaultMerchVariant)) return null;
    return (
      <MerchTypeCard
        primaryDimId={null}
        primaryValueId={null}
        primaryValueLabel={getMultilingualString(product.name, language as Lang)}
        secondaryDims={[]}
        allVariants={allVariants}
        cartVariantQuantities={cartVariantQuantities}
        product={product}
        language={language as Lang}
      />
    );
  }

  const primaryDim = dimensions[0];
  const secondaryDims = dimensions.slice(1);

  return (
    <Flex flexDirection="column" gap="200">
      {primaryDim.values.map(primaryValue => (
        <MerchTypeCard
          key={primaryValue._id}
          primaryDimId={primaryDim._id}
          primaryValueId={primaryValue._id}
          onImageValueChange={onImageValueChange}
          primaryValueLabel={getMultilingualString(
            primaryValue.label,
            language as Lang,
          )}
          secondaryDims={secondaryDims}
          allVariants={allVariants}
          cartVariantQuantities={cartVariantQuantities}
          product={product}
          language={language as Lang}
        />
      ))}
    </Flex>
  );
};

// Generated types mark every nested field optional; the API always returns ids for saved subdocuments.
const getMerchOptions = (product: Product | null) => ({
  dimensions: (product?.merchMetadata?.dimensions ??
    []) as unknown as MerchDimension[],
  variants: (product?.merchMetadata?.variants ??
    []) as unknown as MerchVariant[],
});

type MerchTypeCardProps = {
  /** Null for a product without options, which sells its default variant. */
  primaryDimId: string | null;
  primaryValueId: string | null;
  onImageValueChange?: (valueId: string) => void;
  primaryValueLabel: string;
  secondaryDims: MerchDimension[];
  allVariants: MerchVariant[];
  cartVariantQuantities: Record<string, number>;
  product: Product | null;
  language: Lang;
};

function MerchTypeCard({
  primaryDimId,
  primaryValueId,
  onImageValueChange,
  primaryValueLabel,
  secondaryDims,
  allVariants,
  cartVariantQuantities,
  product,
  language,
}: MerchTypeCardProps) {
  const { t } = useTranslation("booker", { keyPrefix: "MERCH_VARIANT" });
  const { addCartItem, updateCartItem, removeCartItem, cart } =
    useCartContext();

  const variantsForType = useMemo(
    () =>
      allVariants.filter(v =>
        primaryValueId
          ? v.dimensionValueIds.includes(primaryValueId)
          : isDefaultMerchVariant(v),
      ),
    [allVariants, primaryValueId],
  );

  const isEntireTypeSoldOut = useMemo(
    () =>
      variantsForType.length > 0 &&
      variantsForType.every(
        v => v.inventoryCount - (cartVariantQuantities[v._id] ?? 0) <= 0,
      ),
    [variantsForType, cartVariantQuantities],
  );

  const [selectedPerDim, setSelectedPerDim] = useState<
    Record<string, string>
  >({});

  const resolvedVariant = useMemo(() => {
    if (
      secondaryDims.length > 0 &&
      Object.keys(selectedPerDim).length !== secondaryDims.length
    )
      return null;

    const requiredIds = [
      ...(primaryValueId ? [primaryValueId] : []),
      ...Object.values(selectedPerDim),
    ];
    return (
      variantsForType.find(
        v =>
          v.dimensionValueIds.length === requiredIds.length &&
          requiredIds.every(id => v.dimensionValueIds.includes(id)),
      ) ?? null
    );
  }, [primaryValueId, selectedPerDim, secondaryDims.length, variantsForType]);

  const isValueSoldOut = useCallback(
    (dimId: string, valueId: string) => {
      const hypothetical = { ...selectedPerDim, [dimId]: valueId };
      const requiredIds = [
        ...(primaryValueId ? [primaryValueId] : []),
        ...Object.values(hypothetical),
      ];
      const matching = variantsForType.filter(v =>
        requiredIds.every(id => v.dimensionValueIds.includes(id)),
      );
      if (matching.length === 0) return false;
      return matching.every(
        v => v.inventoryCount - (cartVariantQuantities[v._id] ?? 0) <= 0,
      );
    },
    [primaryValueId, selectedPerDim, variantsForType, cartVariantQuantities],
  );

  const cartKey = resolvedVariant
    ? `${product?._id}::${resolvedVariant._id}`
    : null;
  const currentCartItem = cartKey
    ? cart.find(item => item.productId === cartKey)
    : null;
  const currentQty = currentCartItem?.quantity ?? 0;

  const remainingStock = resolvedVariant
    ? resolvedVariant.inventoryCount -
      (cartVariantQuantities[resolvedVariant._id] ?? 0) +
      currentQty
    : 0;

  const imageDim = getImageDimension(product);
  const imageValueId = !imageDim?._id || !primaryDimId
    ? null
    : imageDim._id === primaryDimId
      ? primaryValueId
      : (selectedPerDim[imageDim._id] ?? null);
  const thumbnail = getValueImages(product, imageValueId)[0] ?? null;

  const handleQuantityChange = (qty: number) => {
    if (!resolvedVariant || !product || !cartKey) return;
    if (imageValueId) onImageValueChange?.(imageValueId);

    if (qty === 0 && currentCartItem) {
      removeCartItem(cartKey);
      return;
    }

    if (currentCartItem) {
      updateCartItem(cartKey, {
        quantity: qty,
        total: resolvedVariant.price * qty,
      });
    } else if (qty > 0) {
      addCartItem({
        productId: cartKey,
        productName: getMultilingualString(product.name, language),
        productType: EnumProductType.Merch,
        startDateISO: "",
        endDateISO: "",
        guestsPerTier: [],
        total: resolvedVariant.price * qty,
        quantity: qty,
        merchVariantId: resolvedVariant._id,
        merchVariantLabel: resolvedVariant.label || undefined,
      });
    }
  };

  const lowestPrice =
    variantsForType.length > 0
      ? Math.min(...variantsForType.map(v => v.price))
      : 0;

  const displayPrice = resolvedVariant ? resolvedVariant.price : lowestPrice;

  const unitsLeft = (resolvedVariant ? [resolvedVariant] : variantsForType)
    .reduce(
      (sum, v) =>
        sum +
        Math.max(v.inventoryCount - (cartVariantQuantities[v._id] ?? 0), 0),
      0,
    );
  const showLastFew =
    !isEntireTypeSoldOut &&
    getStockSignal(unitsLeft, product?.merchMetadata?.lowStockThreshold) ===
      "last_few";
  const priceText = `$${formatPrice(displayPrice)}`;

  const cardVisual =
    currentQty > 0
      ? "active"
      : isEntireTypeSoldOut
        ? "soldOut"
        : "default";

  return (
    <MerchCardSurface
      visual={cardVisual}
      onClick={
        imageValueId && onImageValueChange
          ? () => onImageValueChange(imageValueId)
          : undefined
      }
      cursor={imageValueId && onImageValueChange ? "pointer" : undefined}
    >
      <Card.Body bg="white" py="250">
        <Flex flexDirection="column" gap="200" w="full">
          <Card.BodyContent alignItems="center">
            <Flex gap="200" alignItems="center" flex="1" minW="0">
              {thumbnail && (
                <Box
                  flexShrink={0}
                  borderRadius="100"
                  overflow="hidden"
                  borderWidth="1px"
                  borderStyle="solid"
                  borderColor="border.light"
                >
                  <SmartImage
                    image={thumbnail}
                    width={THUMBNAIL_SIZE}
                    height={THUMBNAIL_SIZE}
                    shape="square"
                    alt={primaryValueLabel}
                  />
                </Box>
              )}
              <Card.Title
                mainText={primaryValueLabel}
                subText={
                  isEntireTypeSoldOut
                    ? t("SOLD_OUT")
                    : showLastFew
                      ? `${priceText} · ${t("ONLY_N_LEFT", { count: unitsLeft })}`
                      : priceText
                }
              />
            </Flex>
            <NumericStepper
              value={currentQty}
              onChange={handleQuantityChange}
              min={0}
              max={
                isEntireTypeSoldOut
                  ? currentQty
                  : resolvedVariant
                    ? Math.max(remainingStock, currentQty)
                    : 0
              }
              disabled={isEntireTypeSoldOut ? currentQty === 0 : !resolvedVariant}
            />
          </Card.BodyContent>

          {!isEntireTypeSoldOut && secondaryDims.length > 0 && (
            <Flex gap="150" w="full">
              {secondaryDims.map(dim => {
                const items = dim.values.map(val => {
                  const soldOut = isValueSoldOut(dim._id, val._id);
                  const label = getMultilingualString(val.label, language);
                  return {
                    value: val._id,
                    label: soldOut ? `${label} — ${t("SOLD_OUT")}` : label,
                    disabled: soldOut,
                  };
                });

                const collection = Select.createCollection({ items });

                return (
                  <Select.Root
                    key={dim._id}
                    collection={collection}
                    value={
                      selectedPerDim[dim._id]
                        ? [selectedPerDim[dim._id]]
                        : []
                    }
                    placeholder={getMultilingualString(dim.name, language)}
                    onValueChange={({
                      value,
                    }: {
                      value: string[];
                    }) => {
                      if (value[0]) {
                        setSelectedPerDim(prev => ({
                          ...prev,
                          [dim._id]: value[0],
                        }));
                        if (dim._id === imageDim?._id) {
                          onImageValueChange?.(value[0]);
                        }
                      }
                    }}
                    flex="1"
                    minW="0"
                    css={{
                      "& [data-scope='select'][data-part='trigger']": {
                        bg: "white",
                      },
                    }}
                  >
                    {collection.items.map(item => (
                      <Select.Item
                        key={item.value}
                        item={item}
                        color={
                          item.disabled ? "text.light" : undefined
                        }
                      />
                    ))}
                  </Select.Root>
                );
              })}
            </Flex>
          )}
        </Flex>
      </Card.Body>
    </MerchCardSurface>
  );
}
